import React, { useState } from "react";
import { Container, Grid, Typography, Box } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BoltIcon from "@mui/icons-material/Bolt";
import StarIcon from "@mui/icons-material/Star";
import DiamondIcon from "@mui/icons-material/Diamond";
import { Section, SectionLabel, DP, GradientText, PillButton, OutlineButton, morphShape } from "./HomeStyles";
import { FormControlLabel, Switch } from "@mui/material";
import { useReveal } from "../../hooks/useReveal";
import { useNavigate } from "react-router-dom";
import { keyframes } from "@mui/material";
import {
  PLAN_TERMS, VIDEO_ADDON_ALL_COURSES, basePriceOf, computePlanPrice,
} from "../../utils/planPricing";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";

const shimmerMove = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;
const badgePop = keyframes`
  0%   { transform: scale(0) rotate(-12deg); }
  60%  { transform: scale(1.15) rotate(4deg); }
  100% { transform: scale(1) rotate(0deg); }
`;
const priceFlip = keyframes`
  0%   { transform: rotateX(90deg); opacity: 0; }
  100% { transform: rotateX(0deg);  opacity: 1; }
`;
const featureSlide = keyframes`
  from { opacity: 0; transform: translateX(-12px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const floatIcon = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50%       { transform: translateY(-8px) rotate(5deg); }
`;

const PLAN_IDS = ["starter", "pro", "elite"] as const;
const PLAN_ICONS = [<BoltIcon />, <StarIcon />, <DiamondIcon />];
// Prices live in utils/planPricing.ts so the cards, the picker and checkout
// all quote the same figure — the server re-derives it and rejects mismatches.
const PLAN_MONTHLY = PLAN_IDS.map(basePriceOf);
const PLAN_YEARLY  = PLAN_IDS.map(basePriceOf);
const PLAN_GRADIENTS = ["linear-gradient(135deg, #1565C0, #1976D2)", `linear-gradient(135deg, ${DP.navyprice}, #70a9fe)`, "linear-gradient(135deg, #1565C0, #0D47A1)"];
const PLAN_POPULAR = [false, true, false];
/** True when this plan sells video as one flat add-on rather than per leaflet. */
const sellsVideoAsBundle = (planId: string) => !!PLAN_TERMS[planId]?.videoIsAllOrNothing;

const PlanCard: React.FC<{
  planIdx: number; yearly: boolean; index: number; visible: boolean;
  onSelect: () => void;
  withVideos: boolean;
  onToggleVideos: (next: boolean) => void;
}> = ({ planIdx, yearly, index, visible, onSelect, withVideos, onToggleVideos }) => {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const tp = t.home.pricing;
  const planId = PLAN_IDS[planIdx];
  const plan = tp.plans[planId];
  const popular = PLAN_POPULAR[planIdx];
  const gradient = PLAN_GRADIENTS[planIdx];
  const monthlyPrice = PLAN_MONTHLY[planIdx];
  const yearlyPrice  = PLAN_YEARLY[planIdx];
  const [hovered, setHovered] = useState(false);

  // Only the unlimited plan prices video here; the pick-your-leaflets plans
  // charge per leaflet inside the course picker instead.
  const bundlesVideo = sellsVideoAsBundle(planId);
  const basePrice = yearly ? yearlyPrice : monthlyPrice;
  const price = bundlesVideo
    ? computePlanPrice({ planId, allVideosIncluded: withVideos })
    : basePrice;
  const savings  = Math.round(((monthlyPrice - yearlyPrice) / monthlyPrice) * 100);

  const handleCta = () => {
    if (planId === "starter" || planId === "pro") { onSelect(); return; }
    const paymentState = {
      planId, planName: plan.name, price, billing: yearly ? "yearly" : "monthly",
      allVideosIncluded: bundlesVideo ? withVideos : undefined,
    };
    // Checkout creates the account for visitors, so everyone goes straight
    // there; the session copy survives a refresh on the checkout page.
    sessionStorage.setItem("pending_payment_state", JSON.stringify(paymentState));
    navigate("/payment", { state: paymentState });
  };

  return (
    <Grid size={{ xs: 12, md: 4 }}>
      <Box onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        sx={{ height: "100%", position: "relative", opacity: visible ? 1 : 0, transform: visible ? popular ? hovered ? "translateY(-16px) scale(1.03)" : "translateY(-8px) scale(1.02)" : hovered ? "translateY(-12px) scale(1.01)" : "translateY(0)" : "translateY(48px) scale(0.95)", transition: `all 0.5s cubic-bezier(0.34,1.56,0.64,1) ${0.1 + index * 0.12}s` }}>

        {popular && (
          <Box sx={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: "linear-gradient(135deg, #FF6B35, #FF8C42)", color: "#FFFFFF", fontWeight: 800, fontSize: "0.72rem", letterSpacing: isRTL ? "0.02em" : "0.1em", textTransform: isRTL ? "none" : "uppercase", px: 2.5, py: 0.7, borderRadius: "50px", boxShadow: "0 4px 16px rgba(255,107,53,0.4)", animation: visible ? `${badgePop} 0.6s cubic-bezier(0.34,1.56,0.64,1) ${0.4 + index * 0.12}s both` : "none", zIndex: 10, whiteSpace: "nowrap" }}>
            ⭐ {tp.mostPopular}
          </Box>
        )}

        <Box sx={{ borderRadius: "28px", overflow: "hidden", height: "100%", background: popular ? gradient : DP.white, border: popular ? "2px solid transparent" : hovered ? `2px solid ${DP.bluePale}` : `2px solid ${DP.border}`, boxShadow: popular ? hovered ? "0 40px 80px rgba(10,22,40,0.5), 0 0 0 1px rgba(66,165,245,0.3)" : "0 24px 60px rgba(10,22,40,0.35), 0 0 0 1px rgba(66,165,245,0.15)" : hovered ? `0 24px 60px ${DP.shadow}` : `0 4px 20px rgba(0,0,0,0.05)`, transition: "box-shadow 0.4s ease, border-color 0.4s ease", display: "flex", flexDirection: "column", position: "relative" }}>
          {popular && <Box sx={{ height: "3px", background: "linear-gradient(90deg, #42A5F5, #90CAF9, #42A5F5)", backgroundSize: "200% 100%", animation: `${shimmerMove} 2s linear infinite` }} />}

          <Box sx={{ p: { xs: 3.5, md: 4 }, display: "flex", flexDirection: "column", flexGrow: 1 }}>
            {/* Icon + name */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
              <Box sx={{ width: 48, height: 48, borderRadius: "14px", background: popular ? "rgba(255,255,255,0.15)" : DP.blueBg, display: "flex", alignItems: "center", justifyContent: "center", "& .MuiSvgIcon-root": { color: popular ? "#FFFFFF" : DP.blue, fontSize: "1.4rem" }, animation: hovered ? `${floatIcon} 1.5s ease-in-out infinite` : "none" }}>
                {PLAN_ICONS[planIdx]}
              </Box>
              <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", color: popular ? "#FFFFFF" : DP.textDark }}>
                {plan.name}
              </Typography>
            </Box>

            <Typography sx={{ fontSize: "0.88rem", lineHeight: 1.7, mb: 3, color: popular ? "rgba(255,255,255,0.7)" : DP.textMuted }}>
              {plan.desc}
            </Typography>

            {/* Price */}
            <Box sx={{ mb: 1, perspective: "400px" }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                <Typography sx={{ fontSize: "1rem", fontWeight: 600, mt: 0.5, color: popular ? "rgba(255,255,255,0.6)" : DP.textMuted }}>$</Typography>
                <Typography sx={{ fontSize: { xs: "3.2rem", md: "3.8rem" }, fontWeight: 800, lineHeight: 1, color: popular ? "#FFFFFF" : DP.textDark, letterSpacing: "-0.03em", animation: `${priceFlip} 0.5s ease both` }}>
                  {price}
                </Typography>
                <Box sx={{ mt: "auto", mb: 0.8, ml: 0.5 }}>
                  <Typography sx={{ fontSize: "0.8rem", color: popular ? "rgba(255,255,255,0.55)" : DP.textMuted }}>
                    {t.common.perMonth}
                  </Typography>
                </Box>
              </Box>
              {yearly && (
                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.8, mt: 0.5, mb: 1 }}>
                  <Typography sx={{ fontSize: "0.8rem", color: popular ? "rgba(255,255,255,0.45)" : DP.textMuted, textDecoration: "line-through" }}>
                    ${monthlyPrice}/mo
                  </Typography>
                  <Box sx={{ backgroundColor: popular ? "rgba(76,175,80,0.25)" : "#E8F5E9", color: popular ? "#81C784" : "#2E7D32", fontSize: "0.72rem", fontWeight: 700, px: 1.2, py: 0.3, borderRadius: "30px", border: popular ? "1px solid rgba(76,175,80,0.3)" : "1px solid #A5D6A7" }}>
                    {tp.savePercent(savings)}
                  </Box>
                </Box>
              )}
              {yearly && (
                <Typography sx={{ fontSize: "0.75rem", color: popular ? "rgba(255,255,255,0.45)" : DP.textMuted }}>
                  {tp.billedAnnually}
                </Typography>
              )}
            </Box>

            <Box sx={{ height: "1px", my: 3, background: popular ? "rgba(255,255,255,0.12)" : DP.border }} />

            {/* Features */}
            <Box sx={{ flexGrow: 1, mb: 4 }}>
              {plan.features.map((f, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.2, mb: 1.2, animation: visible ? `${featureSlide} 0.4s ease both` : "none", animationDelay: `${0.3 + index * 0.12 + i * 0.06}s` }}>
                  <Box sx={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, mt: "1px", background: popular ? "rgba(255,255,255,0.18)" : DP.blueBg, display: "flex", alignItems: "center", justifyContent: "center", transition: "transform 0.2s ease", ...(hovered ? { transform: "scale(1.1)" } : {}) }}>
                    <CheckIcon sx={{ fontSize: "0.75rem", color: popular ? "#FFFFFF" : DP.blue }} />
                  </Box>
                  <Typography sx={{ fontSize: "0.88rem", lineHeight: 1.55, color: popular ? "rgba(255,255,255,0.85)" : DP.textMid }}>{f}</Typography>
                </Box>
              ))}
            </Box>

            {/* Companion-video add-on — the unlimited plan buys every video at
                once, so it is priced here rather than per leaflet. */}
            {bundlesVideo && (
              <Box
                sx={{
                  mb: 2.5, px: 1.75, py: 1.25, borderRadius: "14px",
                  border: `1.5px solid ${withVideos ? DP.blue : DP.border}`,
                  backgroundColor: withVideos ? DP.blueBg : "transparent",
                  transition: "all 0.25s ease",
                }}
              >
                <FormControlLabel
                  sx={{ m: 0, width: "100%", justifyContent: "space-between" }}
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={withVideos}
                      onChange={(e) => onToggleVideos(e.target.checked)}
                      inputProps={{ "aria-label": tp.includeVideos }}
                    />
                  }
                  label={
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: DP.textDark, lineHeight: 1.35 }}>
                        {tp.includeVideos}
                      </Typography>
                      <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: DP.blue }}>
                        {tp.includeVideosHint(VIDEO_ADDON_ALL_COURSES)}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}

            {/* CTA */}
            {popular ? (
              <PillButton variant="contained" endIcon={<ArrowForwardIcon sx={{ transform: isRTL ? "scaleX(-1)" : "none" }} />} onClick={handleCta}
                sx={{ backgroundColor: "rgba(255,255,255,0.15)", color: "#FFFFFF", border: "1.5px solid rgba(255,255,255,0.35)", backdropFilter: "blur(8px)", py: 1.8, fontSize: "0.95rem", fontWeight: 700, width: "100%", position: "relative", overflow: "hidden", "&::before": { content: '""', position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))", opacity: hovered ? 1 : 0, transition: "opacity 0.3s ease" }, "&:hover": { backgroundColor: "rgba(255,255,255,0.25)", transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(0,0,0,0.2)" }, transition: "all 0.3s cubic-bezier(0.34,1.56,0.64,1)" }}>
                {plan.cta}
              </PillButton>
            ) : (
              <OutlineButton onClick={handleCta} sx={{ width: "100%", py: 1.8, fontSize: "0.95rem", borderColor: hovered ? DP.blue : DP.border, color: hovered ? DP.white : DP.blue, backgroundColor: hovered ? DP.blue : "transparent", transition: "all 0.3s cubic-bezier(0.34,1.56,0.64,1)" }}>
                {plan.cta}
              </OutlineButton>
            )}
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

const PricingPlans: React.FC = () => {
  const yearly = false;
  const { ref, visible } = useReveal(0.08);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const tp = t.home.pricing;

  // Keyed by plan id — only the unlimited plan reads it today, but keeping it
  // per-plan means a second bundle plan needs no extra state.
  const [videoByPlan, setVideoByPlan] = useState<Record<string, boolean>>({});

  const handlePlanSelect = (idx: number) => {
    const planId = PLAN_IDS[idx];
    // Pick-your-leaflets plans go to their own page, where every leaflet's
    // summary is visible before the buyer chooses.
    if (planId === "starter" || planId === "pro") { navigate(`/choose-leaflets/${planId}`); return; }
    const params = new URLSearchParams({ plan: planId, billing: yearly ? "yearly" : "monthly" });
    navigate(user ? `/subscribe-plan?${params}` : `/signup?redirect=${encodeURIComponent(`/subscribe-plan?${params}`)}`);
  };

  return (
    <Section id="pricing" ref={ref} sx={{ background: `linear-gradient(180deg, ${DP.offWhite} 0%, ${DP.white} 100%)`, position: "relative", overflow: "hidden" }}>
      <Box sx={{ position: "absolute", top: "10%", left: "50%", transform: "translateX(-50%)", width: 800, height: 800, borderRadius: "50%", background: `radial-gradient(circle, ${DP.bluePale}30 0%, transparent 70%)`, animation: `${morphShape} 25s ease-in-out infinite`, pointerEvents: "none" }} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 }, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(28px)", transition: "all 0.8s cubic-bezier(0.22,1,0.36,1)" }}>
          <SectionLabel sx={{ mb: 2 }}>{tp.label}</SectionLabel>
          <Typography variant="h2" sx={{ fontSize: { xs: "2rem", md: "3.2rem" }, fontWeight: 800, color: DP.textDark, letterSpacing: "-0.02em", mb: 2 }}>
            {tp.heading}{" "}<GradientText>{tp.headingHighlight}</GradientText>
          </Typography>
          <Typography sx={{ fontSize: "1.05rem", color: DP.textMuted, maxWidth: 560, mx: "auto", lineHeight: 1.8, mb: 5 }}>
            {tp.subtitle}
          </Typography>

        </Box>

        <Grid container spacing={3} alignItems="stretch">
          {PLAN_IDS.map((id, i) => (
            <PlanCard
              key={i}
              planIdx={i}
              yearly={yearly}
              index={i}
              visible={visible}
              onSelect={() => handlePlanSelect(i)}
              withVideos={!!videoByPlan[id]}
              onToggleVideos={(next) => setVideoByPlan(prev => ({ ...prev, [id]: next }))}
            />
          ))}
        </Grid>

      
      </Container>

    </Section>
  );
};

export default PricingPlans;
